import h from 'hyperscript';

import analysisLayerVisualizationIcon from '@disy/cadenza-icons/analysis-layer.svg';
import chartCombination from '@disy/cadenza-icons/chart-combination.svg';
import chartVisualizationIcon from '@disy/cadenza-icons/chart-line.svg';
import acquisitionLayerVisualizationIcon from '@disy/cadenza-icons/data-acquisition-layer.svg';
import externalLayerIcon from '@disy/cadenza-icons/external-layer.svg';
import sketchIcon from '@disy/cadenza-icons/sketch-layer.svg';
import controllerIcon from '@disy/cadenza-icons/step-control.svg';
import imageVisualizationIcon from '@disy/cadenza-icons/vis-type-image.svg';
import indicatorVisualizationIcon from '@disy/cadenza-icons/vis-type-indicator.svg';
import mapVisualizationIcon from '@disy/cadenza-icons/vis-type-map.svg';
import tableVisualizationIcon from '@disy/cadenza-icons/vis-type-table.svg';
import textVisualizationIcon from '@disy/cadenza-icons/vis-type-text.svg';
import unknownVisualizationIcon from '@disy/cadenza-icons/vis-type-unknown.svg';
import viewsVisualizationIcon from '@disy/cadenza-icons/vis-type-views.svg';
import visualizationTypeIcon from '@disy/cadenza-icons/visualization-type.svg';
import { type StyleClass, addStyleClass } from '@disy/lib/add-style-class';
import type { Icon } from '@disy/ui/icon';
import { icon } from '@disy/ui/icon';
import { LabelWithIcon } from '@disy/ui/label-with-icon';

import type { DataViewVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import { isDataViewVisualizationKey, visualizationIcon } from 'cadenza/workbook/api/data-view-visualization-key';

import i18n from './visualization-type-label.properties';

const COMPONENT_NAME = 'd-visualization-type-label';

export const VISUALIZATION_TYPE_VALUES = [
  'ACQUISITION_LAYER',
  'ANALYSIS_LAYER',
  'CHART',
  'CHART_COMBINATION',
  'CONTROLLER',
  'EXTERNAL_LAYER',
  'IMAGE',
  'OTHER', // indicator
  'MAP',
  'SKETCH_LAYER',
  'TABLE',
  'TEXT',
  'UNKNOWN',
  'VIEW',
  'VISUALIZATION_TYPE',
] as const;
export type VisualizationType = DataViewVisualizationKey | (typeof VISUALIZATION_TYPE_VALUES)[number];

const ICONS_BY_TYPE: Record<(typeof VISUALIZATION_TYPE_VALUES)[number], string> = {
  ACQUISITION_LAYER: acquisitionLayerVisualizationIcon,
  ANALYSIS_LAYER: analysisLayerVisualizationIcon,
  CHART: chartVisualizationIcon,
  CHART_COMBINATION: chartCombination,
  CONTROLLER: controllerIcon,
  EXTERNAL_LAYER: externalLayerIcon,
  IMAGE: imageVisualizationIcon,
  MAP: mapVisualizationIcon,
  OTHER: indicatorVisualizationIcon,
  SKETCH_LAYER: sketchIcon,
  TABLE: tableVisualizationIcon,
  TEXT: textVisualizationIcon,
  UNKNOWN: unknownVisualizationIcon,
  VIEW: viewsVisualizationIcon,
  VISUALIZATION_TYPE: visualizationTypeIcon,
};

export function createVisualizationTypeLabel(
  type: VisualizationType,
  visualizationName: string | HTMLElement,
  { styleClass }: { styleClass?: StyleClass } = {},
) {
  const label = h<VisualizationTypeLabel>(COMPONENT_NAME, { type }, visualizationName);
  addStyleClass(label, styleClass);
  return label;
}

function visualizationType(type: VisualizationType): Icon {
  if (isDataViewVisualizationKey(type)) {
    return visualizationIcon(type);
  }
  return icon(ICONS_BY_TYPE[type], { label: i18n(`visualizationTypeLabel.<${type}>`) });
}

export class VisualizationTypeLabel extends LabelWithIcon {
  constructor() {
    super(() => visualizationType(this.type));
  }

  override connectedCallback() {
    this.largerIcon = true;
    super.connectedCallback();
  }

  get type() {
    return this.getAttribute('type') as VisualizationType;
  }

  set type(value: VisualizationType) {
    this.setAttribute('type', value);
  }
}

customElements.define(COMPONENT_NAME, VisualizationTypeLabel);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: VisualizationTypeLabel;
  }
}
